
// @ is an alias to /src
import { Component, Vue, Prop } from "vue-property-decorator";
import * as Model from "@gigalot/data-models";
import lodash from "lodash";
const Subscriber = require("@jetblack/graphql-reconnect-client");
import { createClient } from "@/helpers/graphql-ws-rtc-adapter";
import FeedDeliverySummary from "@/views/FeedDeliverySummary.vue";
import { DimssaButton } from "@/components/shared/dimssa-button.vue";
import type { ButtonState } from "@/components/shared/dimssa-button.vue";
import ReplayEditDialog from "@/components/ReplayEditDialog.vue";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";
import { pauseJobsService } from "@/helpers/pause-jobs";
import { rationsWithBulkFactorQuery } from "@/helpers/gql";

type RationWithBulkFactor = Model.Ration & {
  bulkFactor?: number | "";
};

@Component({
  components: {
    ReplayEditDialog,
    FeedDeliverySummary,
    DimssaButton,
    BreadcrumbTrail,
  },
})
export default class FeedMixer extends Vue {
  @Prop(Boolean) replay!: boolean;
  name = "FeedMixer";
  loading = true;
  search = "";
  feedPlan: any = {};
  MixingSchedule: any = {};
  tempprogress = 20;
  bufferValue = 100;
  activeServerRequests = 0;

  get serverBusy(): boolean {
    return this.activeServerRequests > 0;
  }

  async withServerActivity<T>(operation: () => Promise<T> | T): Promise<T> {
    this.activeServerRequests += 1;
    try {
      return await Promise.resolve(operation());
    } finally {
      this.activeServerRequests = Math.max(0, this.activeServerRequests - 1);
    }
  }
  tableKey = 0;
  snack = false;
  snackColor = "";
  snackText = "";
  errorText = "";
  errorDialog = false;
  addNewMixDialog = false;
  addNewKraalDialog = false;
  rations: Model.Ration[] = [];
  loaders: Model.Loader[] = [];
  selectedLoader: Model.Loader = new Model.Loader();
  selectedLoadType: string = "Dump";
  selectedRation: any = "";
  selectedMixerCapacity: number = 0;
  selectedFeedAllocation: any = {};
  selectedMix: any = {};
  fixedHeader = [{}];
  availableKraals: Model.MixingScheduleItemAllocation[] = [];
  configuration = {};

  get formattedKraals() {
    return this.availableKraals.map((kraal) => ({
      ...kraal,
      formattedText: `${kraal.kraalID} (${kraal.totalFeedAllocated}kg) - Session ${kraal.session}`,
    }));
  }

  get isGigalotAdmin() {
    try {
      return this.$store.getters["user/isUserGigalotAdmin"]();
    } catch (err) {
      return false;
    }
  }
  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Mix Schedule", disabled: true });
  }

  get pauseButtonState(): ButtonState {
    return pauseJobsService.state.pauseButtonState;
  }

  get pauseStatus() {
    return pauseJobsService.state.pauseStatus;
  }

  get pauseButtonText() {
    return this.pauseStatus === "Paused" ? "Resume Jobs" : "Pause Jobs";
  }

  get pauseChipColor() {
    if (this.pauseStatus === "Paused") return "red";
    if (this.pauseStatus === "Running") return "green";
    return "grey";
  }

  getDeliveryColor(amountRequired: number, amountFed: number) {
    if (amountFed === 0) return "grey";
    else if (((amountRequired - amountFed) / amountRequired) * 100 > 10) {
      return "blue";
    } else if ((Math.abs(amountFed - amountRequired) / amountRequired) * 100 > 10) {
      return "red";
    } else if ((Math.abs(amountFed - amountRequired) / amountRequired) * 100 > 5) {
      return "orange";
    } else return "green";
  }

  headers = [
    {
      text: "Sequence",
      value: "sequence",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 50,
    },
    {
      text: "Scheduled Time",
      value: "time",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 20,
    },
    {
      text: "Mixer",
      value: "mixerGuid",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 50,
    },
    {
      text: "Operator",
      value: "operator",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 50,
    },
    {
      text: "Ration",
      value: "ration",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 50,
    },
    {
      text: "Capacity",
      value: "mixerCapacity",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 100,
    },
    {
      text: "Type",
      value: "loadType",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
      width: 100,
    },
    { text: "", value: "row-operations", width: 20 },
    { text: "", value: "data-table-expand", width: 20 },
  ];
  expanded = [];
  expandAllIssues(items: any, status: any) {
    if (status) {
      this.expanded = items;
    } else {
      this.expanded = [];
    }
  }
  async mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.getFeedMixer);
    this.$store.dispatch("user/addFirebaseCallback", this.getFeedMixerSocket);
    this.$store.dispatch("user/addFirebaseCallback", pauseJobsService.getPauseStatus.bind(pauseJobsService));
    this.rations = await this.getRations();
    this.loaders = lodash.cloneDeep(this.$store.getters["storage"]().Loaders) as Model.Loader[];
    await pauseJobsService.getPauseStatus();

    try {
      console.log("getData()");

      let gql = `query($guid:String!){
        Configuration(guid:$guid)
      }`;
      let json = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
        url: "https://pi.gigalot.systems:7777/feeder",
      });
      console.log("Done Downloading");
      this.configuration = JSON.parse(json.data.Configuration);
    } catch (err) {
      console.log("getData -> error: " + err);
    }
  }
  get moment() {
    return this.$store.state.moment;
  }

  async getRations() {
    try {
      let json = await this.$store.dispatch(
        "graphQl",
        {
          gql: rationsWithBulkFactorQuery,
          variables: { guid: this.$store.state.user.location.guid },
        },
        { root: true }
      );
      return lodash.cloneDeep(json.data.Rations) as Model.Ration[];
    } catch (err) {
      console.log("getRations -> error: " + err);
      return lodash.cloneDeep(this.$store.getters["storage"]().Rations) as Model.Ration[];
    }
  }

  openNewMixDialog() {
    this.updateSelectedMixerCapacity();
    this.addNewMixDialog = true;
  }

  getRationBulkFactor(ration: RationWithBulkFactor | undefined | null) {
    const bulkFactor = Number(ration?.bulkFactor ?? 1);
    return Number.isFinite(bulkFactor) && bulkFactor > 0 ? bulkFactor : 1;
  }

  updateSelectedMixerCapacity() {
    const baseCapacity = Number(this.selectedLoader?.trailer?.maxCapacity ?? 0);
    const adjustedCapacity = baseCapacity / this.getRationBulkFactor(this.selectedRation as RationWithBulkFactor);
    this.selectedMixerCapacity = Number.isFinite(adjustedCapacity) ? Math.round(adjustedCapacity) : 0;
  }

  destroyed() {
    if (this.graphQLUnsubscribe) {
      this.graphQLUnsubscribe();
    }
    if (this.graphQLSubscriber) {
      this.graphQLSubscriber();
    }
  }
  urlws = "wss://pi.gigalot.systems:7778/api/ws";
  options = {};

  subscriptions = `subscription($guid:String!){
        ${this.$props.replay ? "replay" : ""}mixingSchedule(guid:$guid){
          typename
          guid
          date
          MixingScheduleItems{
            typename
            guid
            mixerGuid
            mixerCapacity
            ration
            rationGuid
            loadType
            allocated
            time
            completed
            operator
            feedAllocations{
              typename
              kraalID
              kraalGuid
              rationGuid
              rationCode
              totalFeedAllocated
              totalFeedDelivered
              feedingjobDone
              active
              session
              percentage
              aborted
            }
            }
          }
        }`;

  variables = { guid: this.$store.state.user.location.guid };
  operationName = null;

  graphQLSubscriber: any = undefined;
  graphQLUnsubscribe: any = undefined;

  async togglePauseJobs() {
    const result = await pauseJobsService.togglePauseJobs();
    if (!result) return;

    this.snack = true;
    this.snackColor = result.snackColor;
    this.snackText = result.snackText;
  }

  getLoaderName(guid: string) {
    let tractor = this.loaders.find((loader) => {
      if (loader.guid === guid) return loader;
    });
    if (tractor) return tractor.tractor.registrationNumber;
  }

  async getFeedingKraals(item: Model.MixingScheduleItem) {
    try {
      this.selectedMix = item;
      console.log("MixingSchedule()");
      this.subloading = true;
      let gql = `query($guid:String!,$ration:String!,$tractor:String!){
        feedingKraals(guid:$guid,ration:$ration,tractor:$tractor){
            typename
            kraalID
            kraalGuid
            rationGuid
            rationCode
            totalFeedAllocated
            active
            session
            percentage
         }
        }`;

      let json = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid, ration: item.rationGuid, tractor: item.mixerGuid },
      });
      this.availableKraals = json.data.feedingKraals;
      this.addNewKraalDialog = true;
      this.subloading = false;
    } catch (err) {
      console.log("getFeedMixer -> error: " + err);
    }
  }

  async getFeedMixerSocket() {
    if (this.$store.state.useP2PConn) {
      await this.getOnlineFeedMixerSocket();
    } else {
      await this.getOnsiteFeedMixerSocket();
    }
  }

  async getOnsiteFeedMixerSocket() {
    this.graphQLSubscriber = Subscriber.graphQLReconnectingSubscriber(
      this.urlws,
      this.options,
      (error: any, subscribe: any) => {
        try {
          if (!(error || subscribe)) {
            // Normal closure.
            console.log("connected to socket");
            this.$store.state.wsDialogVisible = false;
            return;
          }
          if (error) {
            console.log("error connecting to socket");
            if (error.event.type === "close") {
            } else {
              this.$store.state.wsDialogVisible = true;
              console.error(error);
            }
          }
          console.log("socket connected");
          this.graphQLUnsubscribe = subscribe(this.subscriptions, this.variables, this.operationName, (error: any, data: any) => {
            if (!(error || subscribe)) {
              console.log("subscribe success");
              // Normal closure
              this.$store.state.wsDialogVisible = false;
              return;
            }
            if (error) {
              console.log("error subscribing");
              if (error.event.type === "close") {
              } else {
                this.$store.state.wsDialogVisible = true;
                console.error(error);
              }
            }
            this.$store.state.wsDialogVisible = false;
            console.log("receiving data");
            const schedule = this.replay ? data?.replaymixingSchedule : data?.mixingSchedule;
            if (!schedule) return;
            this.MixingSchedule = schedule;

            this.loading = false;
          });
        } catch (err) {}
      },
      15000,
      3,
      "graphql-ws"
    );
  }

  async getOnlineFeedMixerSocket() {
    let variables = { guid: this.$store.state.user.location.guid, user: this.$store.state.user.user.uid };

    const subscribe = (subscriptions: string, variables: any, callback: (error: any, data: any) => Promise<void>) => {
      const client = createClient();
      let unsubscribe = client?.subscribe(
        {
          query: subscriptions,
          variables: variables,
        },
        {
          next: (message: any) => callback(undefined, message.data),
          error: (err: any) => {
            throw err;
          },
          complete: () => console.log("subscription completed"),
        }
      );
      return unsubscribe;
    };

    this.graphQLUnsubscribe = subscribe(this.subscriptions, variables, async (error: any, data: any) => {
      this.$store.state.wsConnected = true;
      if (!(error || subscribe)) {
        console.log("subscribe success");
        this.$store.state.wsDialogVisible = false;
        // Normal closure
        return;
      }
      if (error) {
        console.log("error subscribing");

        this.$store.state.wsConnected = false;
        if (error.event.type === "close") {
        } else {
          this.$store.state.wsDialogVisible = true;
          console.error(error);
          //    throw error;
        }
      }
      this.$store.state.wsDialogVisible = false;
      this.$store.state.wsConnected = true;
      console.log("almost there");

      console.log(data);
      const schedule = this.replay ? data?.replaymixingSchedule : data?.mixingSchedule;
      if (!schedule) return;
      this.MixingSchedule = schedule;
      this.loading = false;
    });
  }

  subloading = false;
  getTotalAdded(item: Model.MixingScheduleItem) {
    return item.feedAllocations.reduce((accumulator, i) => {
      return accumulator + i.totalFeedAllocated;
    }, 0);
  }

  getColor(kraalId: string) {
    let itemPlan = lodash.find(this.feedPlan.FeedingPlanItems, { kraalId: kraalId }); //TODO: replace kraal ID with Kraal guid
    return itemPlan?.currentRation?.colour || "grey";
  }

  getSessionColor(session: number) {
    switch (session) {
      case 0:
        return "black";
      case 1:
        return "#80CBC4";
      case 2:
        return "#009688";
      case 3:
        return "#00695C";
      case 4:
        return "#004D40";
    }
    //TODO: make session colors from palette
  }

  async makeNewMix() {
    if (this.replay) return;
    //check if all fields are valid

    if (this.selectedMixerCapacity >= 12000) {
      this.$store.commit("popup/displayYesNo", {
        message: "Are you sure? The amount entered is very large (>12000kg)",
        yesAction: async () => {
          await this.withServerActivity(async () => {
            let url = `${this.$store.getters["backendUrl"]()}/newMix`;
            let jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, {
              root: true,
            });

            let data = {
              capacity: this.selectedMixerCapacity,
              mixerGuid: this.selectedLoader,
              rationCode: this.selectedRation.name,
              rationGuid: this.selectedRation.guid,
              type: this.selectedLoadType,
            };
            data.mixerGuid.trailer.maxCapacity = data.mixerGuid.trailer.maxCapacity;
            try {
              let json = await this.$store.dispatch(
                "graphQl",
                {
                  gql: `mutation newMix($guid:String!, $mixInfo: MixInfoInput!) {
              newMix(guid:$guid, input: $mixInfo) 
        }`,
                  variables: { guid: this.$store.state.user.location.guid, mixInfo: data },
                },
                { root: true }
              );
            } catch (err) {
              console.log(err);
              throw err;
            } finally {
              //close dialog
              this.addNewMixDialog = false;
            }
          });
        },
      });
    } else {
      await this.withServerActivity(async () => {
        let url = `${this.$store.getters["backendUrl"]()}/newMix`;
        let jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, {
          root: true,
        });

        let data = {
          capacity: this.selectedMixerCapacity,
          mixerGuid: this.selectedLoader,
          rationCode: this.selectedRation.name,
          rationGuid: this.selectedRation.guid,
          type: this.selectedLoadType,
        };
        data.mixerGuid.trailer.maxCapacity = data.mixerGuid.trailer.maxCapacity;
        try {
          let json = await this.$store.dispatch(
            "graphQl",
            {
              gql: `mutation newMix($guid:String!, $mixInfo: MixInfoInput!) {
              newMix(guid:$guid, input: $mixInfo) 
        }`,
              variables: { guid: this.$store.state.user.location.guid, mixInfo: data },
            },
            { root: true }
          );
        } catch (err) {
          console.log(err);
          throw err;
        } finally {
          //close dialog
          this.addNewMixDialog = false;
        }
      });
    }
  }

  async getFeedMixer() {
    try {
      console.log("MixingSchedule()");
      this.loading = true;
      let gql = `query($guid:String!){
        MixingSchedule(guid:$guid){
          typename
          guid
          date
          MixingScheduleItems{
            typename
            guid
            mixerGuid
            mixerCapacity
            ration
            rationGuid
            loadType
            allocated
            time
            completed
            operator
            feedAllocations{
              typename
              kraalID
              kraalGuid
              rationGuid
              rationCode
              totalFeedAllocated
              totalFeedDelivered
              feedingjobDone
              active
              session
              percentage
              aborted
            }
          }
        }
        FeedPlan(guid:$guid) {
          FeedingPlanItems{
            kraalId
            numberAnimals
            currentRation{
              colour
            }
            }
          }
        }`;

      let json = await this.$store.dispatch("graphQl", { gql, variables: { guid: this.$store.state.user.location.guid } });
      if (!this.replay) this.MixingSchedule = json.data.MixingSchedule;

      this.feedPlan = json.data.FeedPlan;
      //  this.rationPlan = json.data.RationPlan;
    } catch (err) {
      console.log("getFeedMixer -> error: " + err);
    }
    if (this.MixingSchedule.MixingScheduleItems?.length > 0) this.loading = false;
  }

  getKraalType(guid: string) {
    let kraals = this.$store.getters[`storage`]().Kraals as Model.Kraal[];
    let kraal = lodash.find(kraals, { guid: guid });
    if (kraal) return kraal.kraalType;
  }

  page = 1;
  forceRerender() {
    let pageHolder = this.page;
    console.log(pageHolder);
    this.tableKey += 1;
    this.page = pageHolder;
    console.log(pageHolder);
  }

  async addNewKraaltoSchedule() {
    //Add the maximum amount available to kraal and mix schedule
    try {
      if (this.selectedFeedAllocation && this.selectedMix) {
        let avalableCapacity = this.selectedMix.mixerCapacity - this.getTotalAdded(this.selectedMix);
        if (avalableCapacity === 0) {
          this.addNewKraalDialog = false;
          return;
        }
        if (this.selectedFeedAllocation.totalFeedAllocated > avalableCapacity) this.selectedFeedAllocation.totalFeedAllocated = avalableCapacity;
        this.selectedFeedAllocation.feedingjobDone = false;
        this.selectedFeedAllocation.totalFeedDelivered = 0;
        delete this.selectedFeedAllocation.formattedText;
        this.selectedMix.feedAllocations.push(this.selectedFeedAllocation);
        //Save
        await this.updateServerItem(this.selectedMix);
      }
    } finally {
      this.selectedFeedAllocation = {};
      this.addNewKraalDialog = false;
    }
  }

  async removeKraalFromAllocation(feedkraal: Model.MixingScheduleItemAllocation, item: Model.MixingScheduleItem) {
    lodash.remove(item.feedAllocations, (o) => {
      return o === feedkraal;
    });
    this.forceRerender();
    await this.updateServerItem(item);
    //TODO: remove feedkraal from item
    //save item back to server (mutation)
  }
  async deleteMix(item: Model.MixingScheduleItem) {
    //get latest before update
    await this.withServerActivity(async () => {
      await this.getFeedMixer();
      let index = this.MixingSchedule.MixingScheduleItems.findIndex((a: Model.MixingScheduleItem) => a.guid === item.guid);
      this.MixingSchedule.MixingScheduleItems.splice(index, 1);
      await this.updateServer();
      //save mixSchedule back to server
    });
  }

  cancel() {
    this.snack = true;
    this.snackColor = "blue lighten-1";
    this.snackText = "Canceled";
  }
  close() {}
  save(item: any) {
    this.updateServerItem(item);
  }

  async updateServer() {
    if (this.replay) return;
    return this.withServerActivity(async () => {
      try {
        console.log("updateServer()");

        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation mutateMixSchedule($guid:String!, $input: MixingScheduleInput!) {
            mutateMixSchedule(guid:$guid, input: $input) {
          typename
          guid
          date
          MixingScheduleItems{
            typename
            guid
            mixerGuid
            mixerCapacity
            ration
            rationGuid
            loadType
            allocated
            time
            operator
            feedAllocations{
              typename
              kraalID
              kraalGuid
              rationGuid
              rationCode
              totalFeedAllocated
              totalFeedDelivered
              feedingjobDone
              active
              session
              percentage
              aborted
            }
          }
        }
        }`,
            variables: { guid: this.$store.state.user.location.guid, input: this.MixingSchedule },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server ${err}`;

        throw err;
      }
    });
  }

  async updateServerItem(item: Model.MixingScheduleItem) {
    if (this.replay) return;
    return this.withServerActivity(async () => {
      try {
        console.log("updateServer()");

        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation addMixSchedule($guid:String!, $input: MixingScheduleItemInput!) {
            addMixSchedule(guid:$guid, input: $input) 
        }`,
            variables: { guid: this.$store.state.user.location.guid, input: item },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server ${err}`;

        throw err;
      }
    });
  }
}
